import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../../../context/LanguageContext';
import LanguageSwitcher from '../../Ui/LanguageSwitcher';

export default function HeaderSection() {
    const { t, isRTL } = useLanguage();
    const navigate     = useNavigate();
    const location     = useLocation();

    const [menuOpen,   setMenuOpen]   = useState(false);
    const [activeLink, setActiveLink] = useState('home');
    const [scrolled,   setScrolled]   = useState(false);

    const navLinks = [
        { key: 'home',          label: t('comingSoon.nav.home')          || 'Home',          href: '#about',         route: null          },
        { key: 'features',      label: t('comingSoon.nav.features')      || 'Features',      href: '#features',      route: null          },
        { key: 'ourApp',        label: t('comingSoon.nav.ourApp')        || 'Our App',       href: '#ourApp',        route: null          },
        { key: 'revolutionary', label: t('comingSoon.nav.revolutionary') || 'Revolutionary', href: '#revolutionary', route: null          },
        { key: 'plans',         label: t('comingSoon.nav.plans')         || 'Plans',         href: '#plans',         route: null          },
        { key: 'contact',       label: t('header.nav.contact')           || 'Contact',       href: null,             route: '/contant-us'  },
    ];

    // ── Scroll listener ──────────────────────────────────────────────────────
    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    // ── Mark "contact" active when on /contactUs ─────────────────────────────
    useEffect(() => {
        if (location.pathname === '/contactUs') {
            setActiveLink('contact');
        } else if (location.pathname === '/') {
            // keep section-based active link; default to 'home' on fresh load
            setActiveLink(prev => (prev === 'contact' ? 'home' : prev));
        }
    }, [location.pathname]);

    // ── After navigating to "/" with a pending hash, scroll to target ────────
    useEffect(() => {
        if (location.pathname === '/' && location.state?.scrollTo) {
            const id = location.state.scrollTo;
            // small delay lets the page render before scrolling
            const timer = setTimeout(() => {
                const el = document.getElementById(id);
                if (el) {
                    const y = el.getBoundingClientRect().top + window.scrollY - 80;
                    window.scrollTo({ top: y, behavior: 'smooth' });
                }
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [location]);

    // ── Scroll helper (same-page) ────────────────────────────────────────────
    const scrollToSection = (id) => {
        const el = document.getElementById(id);
        if (el) {
            const y = el.getBoundingClientRect().top + window.scrollY - 80;
            window.scrollTo({ top: y, behavior: 'smooth' });
        }
    };

    // ── Main nav handler ─────────────────────────────────────────────────────
    const handleNav = (key, href, route) => {
        setActiveLink(key);
        setMenuOpen(false);

        // 1. External route (e.g. /contactUs)
        if (route) {
            navigate(route);
            return;
        }

        // 2. Hash anchor
        if (href?.startsWith('#') && href.length > 1) {
            const sectionId = href.slice(1);

            if (location.pathname === '/') {
                // already on home — just scroll
                scrollToSection(sectionId);
            } else {
                // navigate home first, then scroll via location.state
                navigate('/', { state: { scrollTo: sectionId } });
            }
        }
    };

    return (
        <header
            dir={isRTL ? 'rtl' : 'ltr'}
            className={`sticky top-0 z-50 w-full bg-[#f7fcfd] border-b border-gray-200 transition-shadow duration-300 md:py-5 ${
                scrolled ? 'shadow-md' : 'shadow-sm'
            }`}
        >
            {/* ── Desktop bar ── */}
            <div className="max-w-[1200px] mx-auto px-4 xl:px-8 py-2 md:py-3 flex items-center justify-between gap-4">

                {/* Brand */}
                <a href="/" className="flex items-center gap-2.5 no-underline flex-shrink-0">
                    <div>
                        <p className="md:text-3xl font-bold">
                            <span className="text-secondary">Dr</span>
                            <span className="text-primary">Easy</span>
                        </p>
                    </div>
                </a>

                {/* Desktop Nav */}
                <nav className="hidden md:flex items-center gap-1 flex-1 justify-center">
                    {navLinks.map(({ key, label, href, route }) => (
                        <button
                            key={key}
                            onClick={() => handleNav(key, href, route)}
                            className={`
                                relative px-2.5 py-1.5 text-[15px] font-medium whitespace-nowrap
                                bg-transparent border-none cursor-pointer group
                                transition-colors duration-200
                                ${activeLink === key
                                    ? 'text-secondary font-semibold'
                                    : 'text-gray-600 hover:text-secondary'
                                }
                            `}
                        >
                            {label}
                            <span
                                className={`
                                    absolute bottom-0 h-0.5 bg-secondary rounded-full
                                    transition-all duration-300
                                    ${isRTL ? 'right-0' : 'left-0'}
                                    ${activeLink === key ? 'w-full' : 'w-0 group-hover:w-full'}
                                `}
                            />
                        </button>
                    ))}
                </nav>

                {/* Right: Language + Hamburger */}
                <div className="flex items-center gap-3 flex-shrink-0">
                    <span className="hidden md:block">
                        <LanguageSwitcher />
                    </span>

                    <button
                        onClick={() => setMenuOpen(o => !o)}
                        aria-label="Toggle menu"
                        className="md:hidden flex flex-col items-center justify-center gap-[5px] w-[38px] h-[38px] rounded-lg border border-gray-200 bg-white cursor-pointer flex-shrink-0"
                    >
                        <span
                            className="block w-5 h-0.5 bg-gray-500 rounded-full transition-transform duration-250"
                            style={{ transform: menuOpen ? 'translateY(7px) rotate(45deg)' : 'none' }}
                        />
                        <span
                            className="block w-5 h-0.5 bg-gray-500 rounded-full transition-opacity duration-200"
                            style={{ opacity: menuOpen ? 0 : 1 }}
                        />
                        <span
                            className="block w-5 h-0.5 bg-gray-500 rounded-full transition-transform duration-250"
                            style={{ transform: menuOpen ? 'translateY(-7px) rotate(-45deg)' : 'none' }}
                        />
                    </button>
                </div>
            </div>

            {/* ── Mobile Drawer ── */}
            {menuOpen && (
                <div className="md:hidden border-t border-gray-200 bg-[#f7fcfd] px-4 pt-3 pb-5">
                    <div className="flex items-center justify-between mb-3 pb-3 border-b border-gray-100">
                        <span className="text-xs text-gray-400 font-medium uppercase tracking-wide">
                            {isRTL ? 'اللغة' : 'Language'}
                        </span>
                        <LanguageSwitcher />
                    </div>

                    <nav className="flex flex-col gap-1 mb-3">
                        {navLinks.map(({ key, label, href, route }) => (
                            <button
                                key={key}
                                onClick={() => handleNav(key, href, route)}
                                className={`
                                    w-full px-3.5 py-2.5 text-[15px] font-medium
                                    rounded-lg border-none cursor-pointer transition-colors duration-200
                                    ${isRTL ? 'text-right' : 'text-left'}
                                    ${activeLink === key
                                        ? 'bg-secondary/10 text-secondary'
                                        : 'bg-transparent text-gray-700 hover:bg-secondary/10 hover:text-secondary'
                                    }
                                `}
                            >
                                {label}
                            </button>
                        ))}
                    </nav>

                   
                </div>
            )}
        </header>
    );
}